import api from '../services/api';
import toast from './toast';
import { formatDate, formatDateTime, getTodayInSingaporeTimezone } from './dateUtils';

// Dynamic imports to handle missing dependencies gracefully
let jsPDF = null;
let autoTable = null;
let XLSX = null;

const loadDependencies = async () => {
  if (!jsPDF || !autoTable || !XLSX) {
    try {
      const jsPDFModule = await import('jspdf');
      const autoTableModule = await import('jspdf-autotable');
      const XLSXModule = await import('xlsx');
      
      jsPDF = jsPDFModule.default || jsPDFModule.jsPDF;
      autoTable = autoTableModule.default || autoTableModule.autoTable;
      XLSX = XLSXModule.default || XLSXModule;
    } catch (error) {
      console.error('Export dependencies not installed. Run: npm install jspdf jspdf-autotable xlsx');
      toast.error('Export functionality requires additional packages. Please contact your administrator.');
      throw new Error('Export dependencies not available');
    }
  }
  return { jsPDF, autoTable, XLSX };
};

// =============================================================================
// PDF EXPORT UTILITIES
// =============================================================================

/**
 * Generate payroll summary PDF report
 */
export const exportPayrollSummaryPDF = async (payrollRun, statistics) => {
  try {
    const { jsPDF, autoTable } = await loadDependencies();
    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(20);
    doc.text('💰 Payroll Summary Report', 20, 20);
    
    doc.setFontSize(12);
    doc.text(`Run Number: #${payrollRun.run_number}`, 20, 35);
    doc.text(`Period: ${payrollRun.payroll_period?.period_name}`, 20, 45);
    doc.text(`Generated: ${formatDateTime(new Date())}`, 20, 55);
    doc.text(`Status: ${payrollRun.status.toUpperCase()}`, 20, 65);
    
    // Summary Statistics
    const summaryData = [
      ['Total Employees', payrollRun.total_employees_processed?.toString() || '0'],
      ['Total Basic Salary', formatCurrency(payrollRun.total_basic_salary || 0)],
      ['Total Allowances', formatCurrency(payrollRun.total_allowances || 0)],
      ['Total Overtime', formatCurrency(payrollRun.total_overtime || 0)],
      ['Total Gross Pay', formatCurrency(payrollRun.total_gross_pay || 0)],
      ['Total CPF (Employee)', formatCurrency(payrollRun.total_cpf_employee || 0)],
      ['Total CPF (Employer)', formatCurrency(payrollRun.total_cpf_employer || 0)],
      ['Total SDL', formatCurrency(payrollRun.total_sdl || 0)],
      ['Total Income Tax', formatCurrency(payrollRun.total_income_tax || 0)],
      ['Total Deductions', formatCurrency(payrollRun.total_deductions || 0)],
      ['Total Net Pay', formatCurrency(payrollRun.total_net_pay || 0)]
    ];
    
    autoTable(doc, {
      startY: 80,
      head: [['Description', 'Amount']],
      body: summaryData,
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246] },
      styles: { fontSize: 10 }
    });
    
    // Approval Section
    if (payrollRun.approved_by_manager || payrollRun.approved_by_hr || payrollRun.approved_by_finance) {
      let approvalY = doc.lastAutoTable.finalY + 20;
      
      doc.setFontSize(14);
      doc.text('Approval History', 20, approvalY);
      approvalY += 10;
      
      const approvalData = [];
      if (payrollRun.approved_by_manager) {
        approvalData.push([
          'Reporting Officer',
          `${payrollRun.approved_by_manager.first_name} ${payrollRun.approved_by_manager.last_name}`,
          formatDateTime(payrollRun.approved_by_manager_at)
        ]);
      }
      if (payrollRun.approved_by_hr) {
        approvalData.push([
          'HR',
          `${payrollRun.approved_by_hr.first_name} ${payrollRun.approved_by_hr.last_name}`,
          formatDateTime(payrollRun.approved_by_hr_at)
        ]);
      }
      if (payrollRun.approved_by_finance) {
        approvalData.push([
          'Finance',
          `${payrollRun.approved_by_finance.first_name} ${payrollRun.approved_by_finance.last_name}`,
          formatDateTime(payrollRun.approved_by_finance_at)
        ]);
      }
      
      if (approvalData.length > 0) {
        autoTable(doc, {
          startY: approvalY,
          head: [['Role', 'Approved By', 'Date & Time']],
          body: approvalData,
          theme: 'grid',
          headStyles: { fillColor: [16, 185, 129] },
          styles: { fontSize: 10 }
        });
      }
    }
    
    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.text(`Page ${i} of ${pageCount}`, 200, 285, { align: 'right' });
      doc.text('Generated by HR Portal - Confidential', 20, 285);
    }
    
    // Save the PDF
    doc.save(`payroll-summary-${payrollRun.run_number}.pdf`);
    toast.success('Payroll summary exported successfully!');
    
  } catch (error) {
    console.error('PDF export error:', error);
    if (error.message === 'Export dependencies not available') {
      return; // Error already handled
    }
    toast.error('Failed to export PDF report');
  }
};

/**
 * Generate MOM compliance report PDF
 */
export const exportMOMComplianceReportPDF = async (complianceReports) => {
  try {
    const { jsPDF, autoTable } = await loadDependencies();
    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(20);
    doc.text('🛡️ MOM Payslip Compliance Report', 20, 20);
    
    doc.setFontSize(12);
    doc.text(`Generated: ${formatDateTime(new Date())}`, 20, 35);
    doc.text(`Total Employees: ${complianceReports.length}`, 20, 45);
    
    // Calculate compliance rate
    const compliantCount = complianceReports.filter(r => r.is_fully_compliant).length;
    const complianceRate = complianceReports.length > 0 ? (compliantCount / complianceReports.length * 100) : 0;
    
    doc.text(`Compliance Rate: ${complianceRate.toFixed(1)}%`, 20, 55);
    
    // Compliance table
    const tableData = complianceReports.map(report => [
      report.employee_id,
      report.employee_full_name,
      report.compliance_score + '/12',
      report.is_fully_compliant ? 'Yes' : 'No',
      report.compliance_issues.length > 0 ? report.compliance_issues.join(', ') : 'None'
    ]);
    
    autoTable(doc, {
      startY: 70,
      head: [['Employee ID', 'Employee Name', 'Score', 'Compliant', 'Issues']],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [185, 28, 28] },
      styles: { fontSize: 8 },
      columnStyles: {
        4: { cellWidth: 60 } // Issues column wider
      }
    });
    
    // Summary
    const summaryY = doc.lastAutoTable.finalY + 20;
    doc.setFontSize(14);
    doc.text('Summary', 20, summaryY);
    
    const summary = [
      ['Fully Compliant Employees', compliantCount.toString()],
      ['Non-Compliant Employees', (complianceReports.length - compliantCount).toString()],
      ['Overall Compliance Rate', `${complianceRate.toFixed(1)}%`]
    ];
    
    autoTable(doc, {
      startY: summaryY + 10,
      head: [['Metric', 'Value']],
      body: summary,
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246] },
      styles: { fontSize: 10 }
    });
    
    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.text(`Page ${i} of ${pageCount}`, 200, 285, { align: 'right' });
      doc.text('MOM Compliance Report - Confidential', 20, 285);
    }
    
    doc.save(`mom-compliance-report-${getTodayInSingaporeTimezone()}.pdf`);
    toast.success('MOM compliance report exported successfully!');
    
  } catch (error) {
    console.error('Compliance PDF export error:', error);
    if (error.message === 'Export dependencies not available') {
      return; // Error already handled
    }
    toast.error('Failed to export compliance report');
  }
};

// =============================================================================
// EXCEL EXPORT UTILITIES  
// =============================================================================

/**
 * Export employee payroll details to Excel
 */
export const exportEmployeePayrollExcel = async (payrollRunId) => {
  try {
    const { XLSX } = await loadDependencies();
    toast.loading('Preparing payroll data for export...');
    
    const response = await api.get(`/confidential/employee-payrolls/`, {
      params: { payroll_run: payrollRunId, page_size: 50000 },
    });
    const employeePayrolls = response.data.results;
    
    if (!employeePayrolls || employeePayrolls.length === 0) {
      toast.error('No employee payroll data found');
      return;
    }
    
    // Prepare data for Excel
    const excelData = employeePayrolls.map(emp => ({
      'Employee ID': emp.employee?.staff_id || '',
      'Employee Name': `${emp.employee?.first_name || ''} ${emp.employee?.last_name || ''}`.trim(),
              'Department': emp.employee?.department_name || '',
      'Basic Salary (Monthly)': emp.basic_salary_monthly || 0,
      'Basic Salary (Earned)': emp.basic_salary_earned || 0,
      'Allowances Total': emp.allowances_total || 0,
      'Overtime Amount': emp.overtime_amount || 0,
      'Gross Pay': emp.gross_pay || 0,
      'CPF Employee (20%)': emp.cpf_employee_contribution || 0,
      'CPF Employer (17%)': emp.cpf_employer_contribution || 0,
      'SDL Amount': emp.sdl_amount || 0,
      'Income Tax': emp.income_tax_deduction || 0,
      'Other Deductions': emp.other_deductions || 0,
      'Leave Deductions': emp.leave_deductions || 0,
      'Total Deductions': emp.total_deductions || 0,
      'Net Pay': emp.net_pay || 0,
      'Calculation Method': emp.calculation_method || '',
      'MOM Compliant': emp.mom_payslip_compliant ? 'Yes' : 'No',
              'Calculated At': emp.calculated_at ? formatDateTime(emp.calculated_at) : ''
    }));
    
    // Create workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Employee Payroll');
    
    // Auto-width columns
    const colWidths = Object.keys(excelData[0]).map(key => ({ wch: Math.max(key.length, 15) }));
    ws['!cols'] = colWidths;
    
    // Save the file
    const fileName = `employee-payroll-details-${getTodayInSingaporeTimezone()}.xlsx`;
    XLSX.writeFile(wb, fileName);
    
    toast.dismiss();
    toast.success('Employee payroll exported to Excel successfully!');
    
  } catch (error) {
    toast.dismiss();
    console.error('Excel export error:', error);
    if (error.message === 'Export dependencies not available') {
      return; // Error already handled
    }
    toast.error('Failed to export payroll data to Excel');
  }
};

/**
 * Export payroll statistics to Excel
 */
export const exportPayrollStatisticsExcel = async (statistics, payrollRuns) => {
  try {
    const { XLSX } = await loadDependencies();
    const wb = XLSX.utils.book_new();
    
    // Statistics Summary Sheet
    const summaryData = [
      ['Metric', 'Value'],
      ['Total Net Pay', formatCurrency(statistics.total_net_pay || 0)],
      ['Total Gross Pay', formatCurrency(statistics.total_gross_pay || 0)],
      ['Total Employees', statistics.total_employees || 0],
      ['Total Basic Salary', formatCurrency(statistics.total_basic_salary || 0)],
      ['Total Allowances', formatCurrency(statistics.total_allowances || 0)],
      ['Total Overtime', formatCurrency(statistics.total_overtime || 0)],
      ['Total CPF (Employee)', formatCurrency(statistics.total_cpf_employee || 0)],
      ['Total CPF (Employer)', formatCurrency(statistics.total_cpf_employer || 0)],
      ['Total SDL', formatCurrency(statistics.total_sdl || 0)],
      ['Total Income Tax', formatCurrency(statistics.total_income_tax || 0)],
      ['Average Basic Salary', formatCurrency(statistics.avg_basic_salary || 0)],
      ['Payroll Periods Count', statistics.payroll_periods_count || 0],
      ['Compliance Rate', `${statistics.compliance_rate || 0}%`]
    ];
    
    const summaryWs = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary Statistics');
    
    // Payroll Runs Sheet
    if (payrollRuns && payrollRuns.length > 0) {
      const runsData = payrollRuns.map(run => ({
        'Run Number': run.run_number,
        'Period': run.payroll_period?.period_name || '',
        'Status': run.status,
        'Employees': run.total_employees_processed || 0,
        'Basic Salary': run.total_basic_salary || 0,
        'Allowances': run.total_allowances || 0,
        'Overtime': run.total_overtime || 0,
        'Gross Pay': run.total_gross_pay || 0,
        'CPF Employee': run.total_cpf_employee || 0,
        'CPF Employer': run.total_cpf_employer || 0,
        'SDL': run.total_sdl || 0,
        'Income Tax': run.total_income_tax || 0,
        'Total Deductions': run.total_deductions || 0,
        'Net Pay': run.total_net_pay || 0,
        'Run Date': run.run_date ? formatDate(run.run_date) : ''
      }));
      
      const runsWs = XLSX.utils.json_to_sheet(runsData);
      XLSX.utils.book_append_sheet(wb, runsWs, 'Payroll Runs');
    }
    
    // Save the file
    const fileName = `payroll-statistics-${getTodayInSingaporeTimezone()}.xlsx`;
    XLSX.writeFile(wb, fileName);
    
    toast.success('Payroll statistics exported to Excel successfully!');
    
  } catch (error) {
    console.error('Statistics Excel export error:', error);
    if (error.message === 'Export dependencies not available') {
      return; // Error already handled
    }
    toast.error('Failed to export statistics to Excel');
  }
};

// =============================================================================
// CPF SUBMISSION FILE UTILITIES
// =============================================================================

/**
 * Generate CPF submission file in required format
 */
export const exportCPFSubmissionFile = async (payrollRunId) => {
  try {
    const { XLSX } = await loadDependencies();
    toast.loading('Generating CPF submission file...');
    
    const response = await api.get(`/confidential/employee-payrolls/`, {
      params: { payroll_run: payrollRunId, page_size: 50000 },
    });
    const employeePayrolls = response.data.results;
    
    if (!employeePayrolls || employeePayrolls.length === 0) {
      toast.error('No employee data found for CPF submission');
      return;
    }
    
    // CPF file format (simplified - real format would be more complex)
    const cpfData = employeePayrolls.map(emp => ({
      'Employee NRIC/FIN': emp.employee?.nric || '',
      'Employee Name': `${emp.employee?.first_name || ''} ${emp.employee?.last_name || ''}`.trim(),
      'Ordinary Wages': emp.basic_salary_earned || 0,
      'Additional Wages': emp.allowances_total || 0,
      'Employee CPF': emp.cpf_employee_contribution || 0,
      'Employer CPF': emp.cpf_employer_contribution || 0,
      'Total CPF': (emp.cpf_employee_contribution || 0) + (emp.cpf_employer_contribution || 0),
      'Payroll Month': emp.payroll_run?.payroll_period?.period_name || ''
    }));
    
    // Create workbook for CPF submission
    const ws = XLSX.utils.json_to_sheet(cpfData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'CPF Submission');
    
    // Format as required by CPF Board
    const fileName = `cpf-submission-${getTodayInSingaporeTimezone()}.xlsx`;
    XLSX.writeFile(wb, fileName);
    
    toast.dismiss();
    toast.success('CPF submission file generated successfully!');
    
  } catch (error) {
    toast.dismiss();
    console.error('CPF export error:', error);
    if (error.message === 'Export dependencies not available') {
      return; // Error already handled
    }
    toast.error('Failed to generate CPF submission file');
  }
};

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Format currency for display
 */
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-SG', {
    style: 'currency',
    currency: 'SGD'
  }).format(amount || 0);
};

/**
 * Export functions for easy importing
 */
export const exportUtils = {
  exportPayrollSummaryPDF,
  exportMOMComplianceReportPDF,
  exportEmployeePayrollExcel,
  exportPayrollStatisticsExcel,
  exportCPFSubmissionFile
}; 